import React from 'react';

/**
 * PlanCharacteristicsCard - Displays plan features and characteristics
 * in a visually appealing, categorized format
 */
const PlanCharacteristicsCard = ({ characteristics, businessDescription }) => {
  if (!characteristics) {
    return null;
  }

  const {
    plan_category,
    plan_subtype,
    complexity_level,
    complexity_score,
    has_auto_enrollment,
    has_matching,
    has_default_investment,
    is_participant_directed,
    has_brokerage_option,
    likely_safe_harbor,
    safe_harbor_confidence,
    is_controlled_group,
    is_frozen_plan,
    has_modern_features,
    modernization_score,
    pension_code_descriptions
  } = characteristics;

  // Helper to render feature badges
  const renderFeatureBadge = (label, value, type = 'default') => {
    if (value === null || value === undefined) return null;

    const colors = {
      success: 'bg-green-100 text-green-800 border-green-300',
      warning: 'bg-amber-100 text-amber-800 border-amber-300',
      info: 'bg-blue-100 text-blue-800 border-blue-300',
      neutral: 'bg-neutral-100 text-neutral-800 border-neutral-300',
      default: 'bg-neutral-50 text-neutral-700 border-neutral-200'
    };

    const colorClass = colors[type] || colors.default;

    return (
      <div className={`px-3 py-2 rounded-lg border ${colorClass} text-sm font-medium`}>
        {label}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
      </div>
    );
  };

  // Determine complexity color
  const getComplexityColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-neutral-600 bg-neutral-50 border-neutral-200';
    }
  };

  // Determine modernization color
  const getModernizationColor = (score) => {
    if (score >= 7) return 'text-green-600';
    if (score >= 4) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Business Description */}
      {businessDescription && (
        <div className="bg-gradient-to-br from-teal/5 to-teal/10 rounded-xl p-6 border border-teal/20">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-teal-dark mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <div>
              <h4 className="text-sm font-bold text-teal-dark uppercase tracking-wide mb-1">Industry</h4>
              <p className="text-lg font-semibold text-neutral-900">{businessDescription}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plan Category & Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plan_category && (
          <div className="bg-white rounded-xl p-5 border-2 border-neutral-200">
            <p className="text-xs text-neutral-600 font-medium mb-2 uppercase tracking-wide">Plan Category</p>
            <p className="text-lg font-bold text-teal-dark">{plan_category}</p>
          </div>
        )}
        
        {plan_subtype && (
          <div className="bg-white rounded-xl p-5 border-2 border-neutral-200">
            <p className="text-xs text-neutral-600 font-medium mb-2 uppercase tracking-wide">Plan Subtype</p>
            <p className="text-lg font-bold text-teal-dark">{plan_subtype}</p>
          </div>
        )}
      </div>

      {/* Complexity & Modernization Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {complexity_level && (
          <div className={`rounded-xl p-5 border-2 ${getComplexityColor(complexity_level)}`}>
            <p className="text-xs font-medium mb-2 uppercase tracking-wide opacity-80">Plan Complexity</p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold">{complexity_level}</p>
              {complexity_score && (
                <p className="text-sm font-semibold opacity-75">Score: {complexity_score}/10</p>
              )}
            </div>
          </div>
        )}

        {modernization_score !== null && modernization_score !== undefined && (
          <div className="bg-white rounded-xl p-5 border-2 border-neutral-200">
            <p className="text-xs text-neutral-600 font-medium mb-2 uppercase tracking-wide">Modernization</p>
            <div className="flex items-baseline gap-2">
              <p className={`text-2xl font-bold ${getModernizationColor(modernization_score)}`}>
                {modernization_score}/10
              </p>
              {has_modern_features && (
                <span className="text-sm font-semibold text-green-600">Modern Features ✓</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Key Plan Features */}
      <div>
        <h4 className="text-base font-bold text-neutral-900 mb-3">Key Plan Features</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {has_auto_enrollment !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${has_auto_enrollment ? 'bg-green-50 border-green-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {has_auto_enrollment ? (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${has_auto_enrollment ? 'text-green-900' : 'text-neutral-600'}`}>
                  Auto-Enrollment
                </span>
              </div>
            </div>
          )}

          {has_matching !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${has_matching ? 'bg-green-50 border-green-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {has_matching ? (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${has_matching ? 'text-green-900' : 'text-neutral-600'}`}>
                  Employer Match
                </span>
              </div>
            </div>
          )}

          {is_participant_directed !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${is_participant_directed ? 'bg-blue-50 border-blue-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {is_participant_directed ? (
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${is_participant_directed ? 'text-blue-900' : 'text-neutral-600'}`}>
                  Self-Directed
                </span>
              </div>
            </div>
          )}

          {has_default_investment !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${has_default_investment ? 'bg-blue-50 border-blue-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {has_default_investment ? (
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${has_default_investment ? 'text-blue-900' : 'text-neutral-600'}`}>
                  Default Investment
                </span>
              </div>
            </div>
          )}

          {has_brokerage_option !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${has_brokerage_option ? 'bg-purple-50 border-purple-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {has_brokerage_option ? (
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${has_brokerage_option ? 'text-purple-900' : 'text-neutral-600'}`}>
                  Brokerage Window
                </span>
              </div>
            </div>
          )}

          {likely_safe_harbor !== null && (
            <div className={`px-4 py-3 rounded-lg border-2 ${likely_safe_harbor ? 'bg-green-50 border-green-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2">
                {likely_safe_harbor ? (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${likely_safe_harbor ? 'text-green-900' : 'text-neutral-600'}`}>
                  Safe Harbor
                  {safe_harbor_confidence && ` (${safe_harbor_confidence})`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Plan Attributes */}
      {(is_controlled_group !== null || is_frozen_plan !== null) && (
        <div>
          <h4 className="text-base font-bold text-neutral-900 mb-3">Additional Attributes</h4>
          <div className="flex flex-wrap gap-3">
            {is_controlled_group !== null && renderFeatureBadge('Controlled Group', is_controlled_group, is_controlled_group ? 'info' : 'neutral')}
            {is_frozen_plan !== null && renderFeatureBadge('Frozen Plan', is_frozen_plan, is_frozen_plan ? 'warning' : 'neutral')}
          </div>
        </div>
      )}

      {/* Detailed Plan Features from Pension Codes */}
      {pension_code_descriptions && pension_code_descriptions.length > 0 && (
        <div>
          <h4 className="text-base font-bold text-neutral-900 mb-3">Detailed Plan Features</h4>
          <div className="space-y-2">
            {pension_code_descriptions.map((code, idx) => (
              <div key={idx} className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                <div className="flex items-start gap-3">
                  <span className="text-xs font-bold text-teal-dark bg-teal-100 px-2 py-1 rounded">
                    {code.code}
                  </span>
                  <div className="flex-1">
                    <p className="text-xs text-neutral-500 font-medium mb-1">{code.category}</p>
                    <p className="text-sm text-neutral-900">{code.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanCharacteristicsCard;
